import React from 'react';
import {
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export const StyledTextField = ({ label, name, value, onChange, disabled, placeholder, multiline, rows, type, required, error, helperText, ...props }) => {
  const theme = useTheme();
  return (
    <TextField
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      type={type}
      required={required}
      error={error}
      helperText={helperText}
      fullWidth
      variant="outlined"
      sx={{
        mb: 3,
        '& .MuiOutlinedInput-root': {
          borderRadius: 3,
          border: `1px solid ${error ? theme.palette.error.main : alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          fontSize: '0.9rem',
          '& fieldset': { border: 'none' },
          '&:hover': {
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
            boxShadow: `0 4px 16px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.15)}`
          },
          '&.Mui-focused': {
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
            boxShadow: `0 6px 20px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.25)}`
          }
        },
        '& .MuiInputBase-input': {
          fontSize: '0.9rem',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.9rem',
          fontWeight: 500,
          color: error ? theme.palette.error.main : undefined,
          transform: 'translate(14px, 1px) scale(1)',
          '&.Mui-focused': {
            transform: 'translate(14px, -17px) scale(0.75)'
          },
          '&.MuiFormLabel-filled': {
            transform: 'translate(14px, -17px) scale(0.75)'
          }
        }
      }}
      {...props}
    />
  );
};

export const StyledSelect = ({ label, name, value, onChange, disabled, children, error, helperText, required, ...props }) => {
  const theme = useTheme();
  return (
    <FormControl 
      fullWidth 
      error={error}
      sx={{ 
        mb: 3,
        '& .MuiOutlinedInput-root': {
          borderRadius: 3,
          border: `1px solid ${error ? theme.palette.error.main : alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          fontSize: '0.9rem',
          '& fieldset': { border: 'none' },
          '&:hover': {
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
            boxShadow: `0 4px 16px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.15)}`
          },
          '&.Mui-focused': {
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
            boxShadow: `0 6px 20px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.25)}`
          }
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.9rem',
          fontWeight: 500,
          color: error ? theme.palette.error.main : undefined,
          transform: 'translate(14px, 1px) scale(1)',
          '&.Mui-focused': {
            transform: 'translate(14px, -17px) scale(0.75)'
          },
          '&.MuiFormLabel-filled': {
            transform: 'translate(14px, -17px) scale(0.75)'
          }
        }
      }}
      {...props}
    >
      <InputLabel required={required}>{label}</InputLabel>
      <Select
        name={name}
        value={value}
        onChange={onChange}
        label={label}
        disabled={disabled}
      >
        {children}
      </Select>
      {helperText && (
        <Typography variant="caption" sx={{ color: error ? 'error.main' : 'text.secondary', mt: 1, ml: 2 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export const StyledDatePicker = ({ label, value, onChange, disabled, error, helperText, required, ...props }) => {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={value}
        onChange={onChange}
        disabled={disabled}
        sx={{
          mb: 3,
          width: '100%',
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            border: `1px solid ${error ? theme.palette.error.main : alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            fontSize: '0.9rem',
            '& fieldset': { border: 'none' },
            '&:hover': {
              border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
              boxShadow: `0 4px 16px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.15)}`
            },
            '&.Mui-focused': {
              border: `1px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
              boxShadow: `0 6px 20px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.25)}`
            }
          },
          '& .MuiInputBase-input': {
            fontSize: '0.9rem',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            fontWeight: 500,
            color: error ? theme.palette.error.main : undefined,
            transform: 'translate(14px, 1px) scale(1)',
            '&.Mui-focused': {
              transform: 'translate(14px, -17px) scale(0.75)'
            },
            '&.MuiFormLabel-filled': {
              transform: 'translate(14px, -17px) scale(0.75)'
            }
          }
        }}
        slotProps={{
          textField: {
            variant: 'outlined',
            fullWidth: true,
            required: required,
            error: error,
            helperText: helperText
          }
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

// Export UsersSettings component
export { default as UsersSettings } from './UsersSettings';
export { default as ProfileManagement } from './ProfileManagement';